import type { CustomerSegment, UserType } from '@/types';

export type SignupUserType = Extract<UserType, 'individual' | 'advocate'>;

export const PROFESSIONAL_CUSTOMER_SEGMENTS = [
  'independent_advocate',
  'law_firm',
  'in_house_legal',
  'business_litigation',
  'legal_ops_or_clerk',
] as const satisfies readonly CustomerSegment[];

export const LITIGANT_CUSTOMER_SEGMENT = 'individual_litigant' as const satisfies CustomerSegment;
export const EXPLORER_CUSTOMER_SEGMENTS = [
  'student_researcher',
  'other',
] as const satisfies readonly CustomerSegment[];

export const CUSTOMER_SEGMENTS = [
  ...PROFESSIONAL_CUSTOMER_SEGMENTS,
  LITIGANT_CUSTOMER_SEGMENT,
  ...EXPLORER_CUSTOMER_SEGMENTS,
] as const satisfies readonly CustomerSegment[];

export function normalizeSignupUserType(value: unknown): SignupUserType | null {
  if (value === 'advocate' || value === 'individual') return value;
  return null;
}

export function normalizeCustomerSegment(value: unknown): CustomerSegment | null {
  if (typeof value !== 'string') return null;
  return CUSTOMER_SEGMENTS.includes(value as CustomerSegment) ? (value as CustomerSegment) : null;
}

export function isProfessionalCustomerSegment(
  value: CustomerSegment | null | undefined
): value is (typeof PROFESSIONAL_CUSTOMER_SEGMENTS)[number] {
  return !!value && (PROFESSIONAL_CUSTOMER_SEGMENTS as readonly CustomerSegment[]).includes(value);
}

export function isExplorerCustomerSegment(
  value: CustomerSegment | null | undefined
): value is (typeof EXPLORER_CUSTOMER_SEGMENTS)[number] {
  return !!value && (EXPLORER_CUSTOMER_SEGMENTS as readonly CustomerSegment[]).includes(value);
}

export function customerSegmentToSignupUserType(
  value: CustomerSegment | null | undefined
): SignupUserType {
  return isProfessionalCustomerSegment(value) ? 'advocate' : 'individual';
}

export function signupUserTypeLabel(value: SignupUserType | null | undefined): string {
  if (value === 'advocate') return 'Advocate/legal professional';
  if (value === 'individual') return 'Individual/non-legal user';
  return 'Not answered';
}

export function customerSegmentLabel(value: CustomerSegment | null | undefined): string {
  switch (value) {
    case 'independent_advocate':
      return 'Independent advocate';
    case 'law_firm':
      return 'Law firm / chambers';
    case 'in_house_legal':
      return 'In-house counsel / legal team';
    case 'business_litigation':
      return 'Business litigation';
    case 'legal_ops_or_clerk':
      return 'Legal operations / clerk';
    case 'individual_litigant':
      return 'Individual litigant';
    case 'student_researcher':
      return 'Student / researcher';
    case 'other':
      return 'Other';
    default:
      return 'Not answered';
  }
}
